import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';
import { invitationSchema, InvitationFormValues } from '@/types/invitation-schema';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseInvitationAutosaveResult {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  error: string | null;
  retryNow: () => void;
}

const DEBOUNCE_MS = 1500;
const MAX_RETRIES = 4;
const BASE_RETRY_MS = 2000;

// Autosave is restricted to the editable, schema-declared fields.
// is_published is excluded so publishing is an explicit user action, not a typing side-effect.
// Anything not in this set (id, user_id, is_paid, unique_slug, created_at, updated_at, music_url cache, etc.)
// is never sent by autosave, even if it accidentally lands in form state.
const AUTOSAVE_FIELDS: ReadonlySet<keyof InvitationFormValues> = new Set(
  (Object.keys(invitationSchema.shape) as Array<keyof InvitationFormValues>).filter(
    (k) => k !== 'is_published',
  ),
);

const TIME_FIELDS: ReadonlySet<string> = new Set([
  'religious_ceremony_time',
  'civil_ceremony_time',
  'party_time',
]);

const OPTIONAL_TEXT_FIELDS: ReadonlySet<string> = new Set([
  'religious_ceremony_name',
  'religious_ceremony_address',
  'religious_ceremony_map_url',
  'civil_ceremony_name',
  'civil_ceremony_address',
  'civil_ceremony_map_url',
  'party_name',
  'party_address',
  'party_map_url',
  'dresscode',
  'background_music_url',
  'gifts_message',
  'bank_account_holder',
  'bank_account_number',
  'bank_name',
  'gift_registry_url',
]);

const emptyToNull = (value: unknown) =>
  value === '' || value === undefined ? null : value;

const toUpdatePayload = (data: InvitationFormValues): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!AUTOSAVE_FIELDS.has(key as keyof InvitationFormValues)) continue;
    if (key === 'itinerary') {
      payload[key] = value as unknown as Json;
    } else if (TIME_FIELDS.has(key) || OPTIONAL_TEXT_FIELDS.has(key)) {
      payload[key] = emptyToNull(value);
    } else {
      payload[key] = value;
    }
  }
  return payload;
};

/**
 * Autosave hook for the invitation editor.
 *
 * - Subscribes to react-hook-form's watch API, debounces edits (1500ms), and
 *   issues a silent Supabase update of the schema-allowlisted columns only.
 * - Edits that arrive during an in-flight save are preserved: after the current
 *   write finishes, if `pendingValues.current` still points to a newer snapshot,
 *   we re-run instead of clearing.
 * - Network failures retry with exponential backoff up to MAX_RETRIES.
 */
export function useInvitationAutosave(
  invitationId: string | undefined,
  form: UseFormReturn<InvitationFormValues>,
  enabled: boolean,
): UseInvitationAutosaveResult {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCount = useRef(0);
  const pendingValues = useRef<InvitationFormValues | null>(null);
  const inFlight = useRef(false);
  const networkErrorReported = useRef(false);

  const performSave = useCallback(async () => {
    if (!invitationId || !enabled) return;
    if (inFlight.current) return;

    const values = pendingValues.current;
    if (!values) return;

    inFlight.current = true;
    setStatus('saving');
    setError(null);

    const payload = toUpdatePayload(values);
    const { error: updateError } = await supabase
      .from('wedding_invitations')
      .update(payload)
      .eq('id', invitationId);

    inFlight.current = false;

    if (updateError) {
      retryCount.current += 1;
      setStatus('error');
      setError(updateError.message);
      if (retryCount.current <= MAX_RETRIES) {
        const delay = BASE_RETRY_MS * 2 ** (retryCount.current - 1);
        retryTimer.current = setTimeout(() => {
          performSave();
        }, delay);
      } else if (!networkErrorReported.current) {
        networkErrorReported.current = true;
        toast.error(
          'No se pudo guardar automáticamente. Revisa tu conexión e inténtalo de nuevo.',
        );
      }
      return;
    }

    retryCount.current = 0;
    networkErrorReported.current = false;

    // Preserve any edits that landed during the await. pendingValues.current is
    // reassigned to a fresh getValues() snapshot on every keystroke, so a
    // reference compare detects new edits unambiguously.
    if (pendingValues.current === values) {
      pendingValues.current = null;
      setStatus('saved');
      setLastSavedAt(new Date());
      queryClient.invalidateQueries({
        queryKey: ['invitation', invitationId],
        refetchType: 'none',
      });
    } else {
      setLastSavedAt(new Date());
      // Newer edits arrived mid-flight; flush them now without waiting for debounce.
      performSave();
    }
  }, [invitationId, enabled, queryClient]);

  useEffect(() => {
    if (!invitationId || !enabled) return;

    const subscription = form.watch((_value, info) => {
      if (!info?.name) return;
      pendingValues.current = form.getValues();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        performSave();
      }, DEBOUNCE_MS);
    });

    return () => {
      subscription.unsubscribe();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [invitationId, enabled, form, performSave]);

  const retryNow = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    retryCount.current = 0;
    networkErrorReported.current = false;
    performSave();
  }, [performSave]);

  return useMemo(
    () => ({ status, lastSavedAt, error, retryNow }),
    [status, lastSavedAt, error, retryNow],
  );
}
